import {calculateErrorMessage} from 'common/helpers'

import type {DOMCache} from './walkthroughDomCache'

export interface ElementFindResult {
  success: boolean
  element: HTMLElement | null
  message: string
  messageId?: string
  messageParams?: Record<string, any>
  selector?: string
  strategy?: string
}

interface ElementFinderConfig {
  timeout?: number
  pollInterval?: number
  retryStrategies?: boolean
  verbose?: boolean
  cache?: DOMCache | null
}

const DEFAULT_FINDER_CONFIG: Required<ElementFinderConfig> = {
  timeout: 5000,
  pollInterval: 100,
  retryStrategies: true,
  verbose: false,
  cache: null,
}

export async function findElement(
  selector: string,
  config?: ElementFinderConfig
): Promise<ElementFindResult> {
  const finalConfig = {...DEFAULT_FINDER_CONFIG, ...config}

  if (selector.startsWith('js:')) {
    return findElementByCustomSelector(selector, finalConfig)
  }

  const parsed = parseSelector(selector)
  if (parsed.type === 'custom' && parsed.handler) {
    return findElementByCustomHandler(parsed.handler, finalConfig)
  }

  return findElementByCssSelector(selector, finalConfig)
}

export function resolveWalkthroughTargetSync(selector: string): HTMLElement | null {
  try {
    if (!selector) return null

    if (selector.startsWith('js:')) {
      const jsCode = selector.substring(3).trim()
      const handler = customSelectorHandlers[jsCode]
      if (!handler) return null
      const element = handler()
      return element instanceof HTMLElement ? element : null
    }

    const parsed = parseSelector(selector)
    if (parsed.type === 'custom' && parsed.handler) {
      const element = parsed.handler()
      return element instanceof HTMLElement ? element : null
    }

    return document.querySelector(selector) as HTMLElement | null
  } catch (err) {
    calculateErrorMessage(err)
    return null
  }
}

type ParsedSelector =
  | {type: 'css'; selector: string}
  | {type: 'custom'; selector: string; handler: () => HTMLElement | null}

function parseSelector(selector: string): ParsedSelector {
  const containsMatch = selector.match(/^(.+):contains\(['"](.+?)['"]\)$/)
  if (containsMatch) {
    const base = containsMatch[1].trim()
    const text = containsMatch[2]
    return {
      type: 'custom',
      selector,
      handler: () => {
        const elements = Array.from(document.querySelectorAll(base)) as HTMLElement[]
        return elements.find((el) => (el.textContent || '').includes(text)) || null
      },
    }
  }

  if (selector.endsWith(':visible')) {
    const base = selector.replace(/:visible$/, '').trim()
    return {
      type: 'custom',
      selector,
      handler: () => {
        const elements = Array.from(document.querySelectorAll(base)) as HTMLElement[]
        return elements.find((el) => isElementVisible(el)) || null
      },
    }
  }

  return {type: 'css', selector}
}

async function findElementByCustomHandler(
  handler: () => HTMLElement | null,
  config: Required<ElementFinderConfig>
): Promise<ElementFindResult> {
  return new Promise((resolve) => {
    const startTime = Date.now()
    const check = () => {
      try {
        const element = handler()
        if (element) {
          resolve({
            success: true,
            element,
            message: 'Element found (custom handler)',
            messageId: 'WALKTHROUGH.DOM.ELEMENT_FOUND_CUSTOM_HANDLER',
            messageParams: {},
            selector: undefined,
            strategy: 'custom',
          })
          return
        }

        if (Date.now() - startTime > config.timeout) {
          resolve({
            success: false,
            element: null,
            message: 'Timeout: Element not found (custom handler)',
            messageId: 'WALKTHROUGH.DOM.TIMEOUT_ELEMENT_NOT_FOUND_CUSTOM_HANDLER',
            messageParams: {ms: config.timeout},
            selector: undefined,
            strategy: 'custom',
          })
          return
        }

        setTimeout(check, config.pollInterval)
      } catch (err) {
        resolve({
          success: false,
          element: null,
          message: `Error in custom handler: ${err instanceof Error ? err.message : String(err)}`,
          messageId: 'WALKTHROUGH.DOM.ERROR_IN_CUSTOM_HANDLER',
          messageParams: {
            error: err instanceof Error ? err.message : String(err),
          },
          selector: undefined,
          strategy: 'custom',
        })
      }
    }

    check()
  })
}

async function findElementByCssSelector(
  selector: string,
  config: Required<ElementFinderConfig>
): Promise<ElementFindResult> {
  return new Promise((resolve) => {
    const startTime = Date.now()

    const checkElement = () => {
      try {
        if (config.cache) {
          try {
            const idMatch = selector.match(/^#([a-zA-Z0-9_\-:.]+)$/)
            if (idMatch) {
              const id = idMatch[1]
              const el = config.cache.idsTree.get(id) || null
              if (el) {
                resolve({
                  success: true,
                  element: el,
                  message: `Element found in cache by id: ${id}`,
                  messageId: 'WALKTHROUGH.DOM.ELEMENT_FOUND_CACHE_ID',
                  messageParams: {id},
                  selector,
                  strategy: 'cache-id',
                })
                return
              }
            }
            const classMatch = selector.match(/^\.([a-zA-Z0-9_-]+)$/)
            if (classMatch) {
              const cls = classMatch[1]
              const list = config.cache.classesTree.get(cls) || []
              if (list.length > 0) {
                const visible = list.find((el) => isElementVisible(el)) || list[0]
                if (visible) {
                  resolve({
                    success: true,
                    element: visible,
                    message: `Element found in cache by class: .${cls}`,
                    messageId: 'WALKTHROUGH.DOM.ELEMENT_FOUND_CACHE_CLASS',
                    messageParams: {class: cls},
                    selector,
                    strategy: 'cache-class',
                  })
                  return
                }
              }
            }
          } catch (cacheErr) {
            calculateErrorMessage(cacheErr)
          }
        }

        const element = document.querySelector(selector) as HTMLElement | null

        if (element) {
          resolve({
            success: true,
            element,
            message: `Element found with selector: ${selector}`,
            messageId: 'WALKTHROUGH.DOM.ELEMENT_FOUND_SELECTOR',
            messageParams: {selector},
            selector,
            strategy: 'css',
          })
          return
        }

        if (Date.now() - startTime > config.timeout) {
          if (config.verbose) {
            // ignore
          }

          resolve({
            success: false,
            element: null,
            message: `Timeout: Element not found with selector: ${selector}`,
            messageId: 'WALKTHROUGH.DOM.TIMEOUT_ELEMENT_NOT_FOUND_SELECTOR',
            messageParams: {selector, ms: config.timeout},
            selector,
            strategy: 'css',
          })
          return
        }

        setTimeout(checkElement, config.pollInterval)
      } catch (error) {
        resolve({
          success: false,
          element: null,
          message: `Invalid CSS selector: ${selector}`,
          messageId: 'WALKTHROUGH.DOM.INVALID_CSS_SELECTOR',
          messageParams: {
            selector,
            error: error instanceof Error ? error.message : String(error),
          },
          selector,
          strategy: 'css',
        })
      }
    }
    checkElement()
  })
}

async function findElementByCustomSelector(
  selector: string,
  config: Required<ElementFinderConfig>
): Promise<ElementFindResult> {
  const jsCode = selector.substring(3).trim()

  return new Promise((resolve) => {
    const startTime = Date.now()
    const checkElement = () => {
      try {
        let element: HTMLElement | null = null
        const handler = customSelectorHandlers[jsCode]
        if (handler) {
          element = handler()
        }

        if (element && element instanceof HTMLElement) {
          resolve({
            success: true,
            element,
            message: `Element found with custom selector: ${jsCode}`,
            messageId: 'WALKTHROUGH.DOM.ELEMENT_FOUND_CUSTOM_SELECTOR',
            messageParams: {jsCode},
            selector,
            strategy: 'javascript',
          })
          return
        }
        if (!handler) {
          resolve({
            success: false,
            element: null,
            message: `Unsupported custom selector handler: ${jsCode}`,
            messageId: 'WALKTHROUGH.DOM.UNSUPPORTED_CUSTOM_SELECTOR_HANDLER',
            messageParams: {jsCode, supported: Object.keys(customSelectorHandlers)},
            selector,
            strategy: 'javascript',
          })
          return
        }
        if (Date.now() - startTime > config.timeout) {
          resolve({
            success: false,
            element: null,
            message: `Timeout: Element not found with custom selector: ${jsCode}`,
            messageId: 'WALKTHROUGH.DOM.TIMEOUT_ELEMENT_NOT_FOUND_CUSTOM_SELECTOR',
            messageParams: {jsCode, ms: config.timeout},
            selector,
            strategy: 'javascript',
          })
          return
        }
        setTimeout(checkElement, config.pollInterval)
      } catch (error) {
        resolve({
          success: false,
          element: null,
          message: `Error executing custom selector: ${error instanceof Error ? error.message : String(error)}`,
          messageId: 'WALKTHROUGH.DOM.ERROR_EXECUTING_CUSTOM_SELECTOR',
          messageParams: {
            error: error instanceof Error ? error.message : String(error),
            jsCode,
          },
          selector,
          strategy: 'javascript',
        })
      }
    }
    checkElement()
  })
}

const customSelectorHandlers: Record<string, () => HTMLElement | null> = {
  findAddButton: () => {
    const buttons = Array.from(document.querySelectorAll('button, .btn'))
    return buttons.find((btn) => {
      const text = btn.textContent?.toLowerCase() || ''
      return text.includes('add') && isElementVisible(btn as HTMLElement)
    }) as HTMLElement | null
  },
  findSaveButton: () => {
    const buttons = Array.from(document.querySelectorAll('button, .btn'))
    return buttons.find((btn) => {
      const text = btn.textContent?.toLowerCase() || ''
      return text.includes('save') && isElementVisible(btn as HTMLElement)
    }) as HTMLElement | null
  },
  findSubmitButton: () => {
    const buttons = Array.from(document.querySelectorAll('button[type="submit"], .btn-primary'))
    return buttons.find((btn) => isElementVisible(btn as HTMLElement)) as HTMLElement | null
  },
  findFirstInput: () => {
    const inputs = Array.from(
      document.querySelectorAll('input, textarea, [role="textbox"]')
    ) as HTMLElement[]
    return inputs.find((input) => isElementVisible(input as HTMLElement)) as HTMLElement | null
  },
}

function isElementVisible(element: HTMLElement): boolean {
  if (!element) return false

  const style = window.getComputedStyle(element)
  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    style.opacity !== '0' &&
    element.offsetWidth > 0 &&
    element.offsetHeight > 0
  )
}

export function injectWalkthroughAttributes(
  element: HTMLElement,
  stepId: string,
  additionalData?: Record<string, string>
): void {
  element.setAttribute('data-walkthrough-step', stepId)

  element.classList.add('walkthrough-target')

  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      element.setAttribute(`data-walkthrough-${key}`, value)
    })
  }
}

export function removeWalkthroughAttributes(element: HTMLElement): void {
  element.removeAttribute('data-walkthrough-step')
  element.classList.remove('walkthrough-target')
  const attributes = Array.from(element.attributes)
  attributes.forEach((attr) => {
    if (attr.name.startsWith('data-walkthrough-')) {
      element.removeAttribute(attr.name)
    }
  })
}

export function cleanupAllWalkthroughAttributes(): void {
  try {
    const allEls = Array.from(document.querySelectorAll<HTMLElement>('*'))

    allEls.forEach((el) => {
      try {
        const attrs = Array.from(el.attributes || [])
        attrs.forEach((attr) => {
          try {
            if (attr.name && attr.name.startsWith('data-walkthrough')) {
              el.removeAttribute(attr.name)
            }
          } catch (err) {
            calculateErrorMessage(err)
          }
        })

        const classes = Array.from(el.classList || [])
        classes.forEach((cls) => {
          try {
            if (
              typeof cls === 'string' &&
              (cls.startsWith('walkthrough-') || cls === 'walkthrough-target')
            ) {
              el.classList.remove(cls)
            }
          } catch (err) {
            calculateErrorMessage(err)
          }
        })

        try {
          el.style.pointerEvents = ''
          el.style.zIndex = ''
        } catch (err) {
          calculateErrorMessage(err)
        }
      } catch (err) {
        calculateErrorMessage(err)
      }
    })

    const overlaySelector =
      '[class*="walkthrough-"], .walkthrough-highlight, .walkthrough-tooltip, .walkthrough-backdrop, .walkthrough-loading-overlay, .walkthrough-transition-loader'
    const overlayNodes = Array.from(document.querySelectorAll<HTMLElement>(overlaySelector))
    overlayNodes.forEach((node) => {
      try {
        if (node && node.parentElement) node.remove()
      } catch (err) {
        calculateErrorMessage(err)
      }
    })

    const portalRoot = document.getElementById('walkthrough-overlay-portal-root')
    if (portalRoot) {
      try {
        portalRoot.remove()
      } catch (err) {
        calculateErrorMessage(err)
      }
    }

    try {
      document.body.style.pointerEvents = ''
      document.body.style.overflow = ''
    } catch (err) {
      calculateErrorMessage(err)
    }
  } catch (err) {
    calculateErrorMessage(err)
  }
}

export async function findAndTagElement(
  selector: string,
  stepId: string,
  config?: ElementFinderConfig
): Promise<ElementFindResult> {
  const result = await findElement(selector, config)

  if (result.success && result.element) {
    injectWalkthroughAttributes(result.element, stepId)
  }

  return result
}

export async function findAndTagMultipleElements(
  selectors: Array<{selector: string; stepId: string}>,
  config?: ElementFinderConfig
): Promise<ElementFindResult[]> {
  const promises = selectors.map(({selector, stepId}) =>
    findAndTagElement(selector, stepId, config)
  )
  return Promise.all(promises)
}

export function getElementBounds(element: HTMLElement): DOMRect {
  return element.getBoundingClientRect()
}

export function scrollToElement(element: HTMLElement, offset: number = 100): void {
  const elementRect = element.getBoundingClientRect()
  const absoluteElementTop = elementRect.top + (window.scrollY || window.pageYOffset)
  const middle = absoluteElementTop - offset

  window.scrollTo({
    top: middle,
    behavior: 'smooth',
  })
}

export function isElementInViewport(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect()
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

export function registerCustomSelector(name: string, handler: () => HTMLElement | null): void {
  customSelectorHandlers[name] = handler
}

export function getRegisteredCustomSelectors(): string[] {
  return Object.keys(customSelectorHandlers)
}

export interface ObserverOptions {
  root?: HTMLElement
  timeout?: number
  pollInterval?: number
  verbose?: boolean
}

export async function observeForSelector(
  selector: string,
  options: ObserverOptions = {}
): Promise<ElementFindResult> {
  const {root = document.body, timeout = 10000, pollInterval = 100} = options

  const immediateResult =
    selector && !selector.startsWith('js:')
      ? (() => {
          try {
            return root.querySelector(selector) as HTMLElement | null
          } catch {
            return resolveWalkthroughTargetSync(selector)
          }
        })()
      : resolveWalkthroughTargetSync(selector)
  if (immediateResult) {
    return {
      success: true,
      element: immediateResult,
      message: `Element found immediately: ${selector}`,
      messageId: 'WALKTHROUGH.DOM.ELEMENT_FOUND_IMMEDIATE',
      messageParams: {selector},
      selector,
      strategy: 'observer-immediate',
    }
  }

  return new Promise((resolve) => {
    let observer: MutationObserver | null = null
    let pollTimer: NodeJS.Timeout | null = null
    let timeoutTimer: NodeJS.Timeout | null = null
    let resolved = false

    const cleanup = () => {
      if (observer) {
        observer.disconnect()
        observer = null
      }
      if (pollTimer) {
        clearInterval(pollTimer)
        pollTimer = null
      }
      if (timeoutTimer) {
        clearTimeout(timeoutTimer)
        timeoutTimer = null
      }
    }

    const resolveOnce = (result: ElementFindResult) => {
      if (resolved) return
      resolved = true
      cleanup()

      resolve(result)
    }

    observer = new MutationObserver(() => {
      const element =
        selector && !selector.startsWith('js:')
          ? (() => {
              try {
                return root.querySelector(selector) as HTMLElement | null
              } catch {
                return resolveWalkthroughTargetSync(selector)
              }
            })()
          : resolveWalkthroughTargetSync(selector)
      if (element) {
        resolveOnce({
          success: true,
          element,
          message: `Element found via MutationObserver: ${selector}`,
          messageId: 'WALKTHROUGH.DOM.ELEMENT_FOUND_MUTATION',
          messageParams: {selector},
          selector,
          strategy: 'observer-mutation',
        })
      }
    })

    try {
      observer.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'id', 'style'],
        characterData: false,
      })
    } catch (err) {
      resolveOnce({
        success: false,
        element: null,
        message: `Failed to setup observer: ${err instanceof Error ? err.message : String(err)}`,
        messageId: 'WALKTHROUGH.DOM.OBSERVER_SETUP_FAILED',
        messageParams: {
          error: err instanceof Error ? err.message : String(err),
        },
        selector,
        strategy: 'observer-error',
      })
      return
    }

    pollTimer = setInterval(() => {
      const element =
        selector && !selector.startsWith('js:')
          ? (() => {
              try {
                return root.querySelector(selector) as HTMLElement | null
              } catch {
                return resolveWalkthroughTargetSync(selector)
              }
            })()
          : resolveWalkthroughTargetSync(selector)
      if (element) {
        resolveOnce({
          success: true,
          element,
          message: `Element found via polling: ${selector}`,
          messageId: 'WALKTHROUGH.DOM.ELEMENT_FOUND_POLL',
          messageParams: {selector},
          selector,
          strategy: 'observer-poll',
        })
      }
    }, pollInterval)

    timeoutTimer = setTimeout(() => {
      resolveOnce({
        success: false,
        element: null,
        message: `Observation timeout: Element not found after ${timeout}ms: ${selector}`,
        messageId: 'WALKTHROUGH.DOM.OBSERVATION_TIMEOUT',
        messageParams: {selector, ms: timeout},
        selector,
        strategy: 'observer-timeout',
      })
    }, timeout)
  })
}
